import type {
    CropTypeDto,
    CropTypesDto,
} from "@campaign/data/dto/crop-type.dto";
import {
    CropTypeDtoSchema,
    CropTypesDtoSchema,
} from "@campaign/data/dto/crop-type.dto";
import { CropType, CropTypes } from "@campaign/domain/models/crop-type.model";
import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import { injectable } from "inversify";
import { err, Result } from "neverthrow";

@injectable()
export class CropTypeMapper {
    map(
        cropTypeDto: CropTypeDto,
    ): Result<CropType, ModelError<typeof CropType>> {
        const cropTypeDtoParsed = CropTypeDtoSchema.safeParse(cropTypeDto);

        if (!cropTypeDtoParsed.success) {
            console.error(cropTypeDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        return CropType.createWithRecovery({
            id: cropTypeDto.id,
            code: cropTypeDto.code,
            agents: cropTypeDto.agents,
            createAt: cropTypeDto.create_at,
            updateAt: cropTypeDto.update_at,
        });
    }

    mapList(cropTypesDto: CropTypesDto): CropTypes {
        const cropTypesDtoParsed = CropTypesDtoSchema.safeParse(cropTypesDto);

        if (!cropTypesDtoParsed.success) {
            console.error(cropTypesDtoParsed.error.issues);
            return CropTypes.empty();
        }

        const cropTypes = cropTypesDto.mapNotNull((cropTypeDto) =>
            this.map(cropTypeDto).match(
                (cropType) => cropType,
                (error) => {
                    if (!error.recuperable) {
                        console.error(
                            "Non-recoverable error mapping crop types:",
                            error,
                        );
                    }
                    return null;
                },
            ),
        );

        return CropTypes.create({
            cropTypes,
            count: cropTypes.length,
        }).unwrapOr(CropTypes.empty());
    }
}
